import React from "react";
import { Settings2 } from "lucide-react";

import { retrieveUserProfile, updateUserProfile } from "../../../api/account";
import Modal from "../../../shared/ui/Modal";
import type { AuthContextType } from "../../auth/AuthContext";

type Site = "danawa" | "enuri" | "naver";
type DisabledSites = Partial<Record<Site, true>>;

const sites: { key: Site; label: string }[] = [
  { key: "danawa", label: "다나와" },
  { key: "enuri", label: "에누리" },
  { key: "naver", label: "네이버" },
];

const CompareSiteSettings = ({ auth }: { auth: AuthContextType }) => {
  const [show, setShow] = React.useState(false);
  const [disabledSites, setDisabledSites] = React.useState<DisabledSites | null>(null);
  const [saving, setSaving] = React.useState<Site | null>(null);
  const [error, setError] = React.useState("");

  const open = () => {
    setShow(true);
    setDisabledSites(null);
    setError("");
    void retrieveUserProfile({
      auth,
      setResult: (profile) => setDisabledSites(profile.compare_sites_disabled ?? {}),
      setErrorMessage: (message) => setError(String(message)),
    });
  };

  const toggle = (site: Site) => {
    if (!disabledSites || saving) return;
    setSaving(site);
    setError("");
    const nextDisabledSites = { ...disabledSites };
    if (nextDisabledSites[site]) {
      delete nextDisabledSites[site];
    } else {
      nextDisabledSites[site] = true;
    }
    void updateUserProfile({
      auth,
      payload: { compare_sites_disabled: nextDisabledSites },
      setResult: (profile) => {
        const saved = profile.compare_sites_disabled;
        if (!saved || sites.some(({ key }) => Boolean(saved[key]) !== Boolean(nextDisabledSites[key]))) {
          setError("활성 상태를 저장하지 못했습니다. 잠시 후 다시 시도해주세요.");
        } else {
          window.dispatchEvent(new CustomEvent("compare-sites-disabled-changed", { detail: saved }));
        }
        setDisabledSites(saved ?? disabledSites);
        setSaving(null);
      },
      setErrorMessage: (message) => {
        setError(String(message));
        setSaving(null);
      },
    });
  };

  return <>
    <button type="button" className="sidebar-site-settings-trigger" onClick={open}
      aria-label="비교사이트 활성 설정" title="비교사이트 활성 설정">
      <Settings2 aria-hidden="true" />
    </button>
    <Modal show={show} onHide={() => setShow(false)} size="sm">
      <Modal.Header closeButton>
        <Modal.Title>비교사이트 활성 설정</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-4">실행 중 활성화된 비교사이트 상품만 처리합니다.<br />비활성화된 상품은 처리하지 않습니다.</p>
        {error && <div className="alert alert-danger" role="alert">{error}</div>}
        {!disabledSites && !error && <p>설정을 불러오는 중입니다.</p>}
        {disabledSites && sites.map(({ key, label }) => (
          <div className="form-check form-switch mb-3" key={key}>
            <input className="form-check-input" type="checkbox" role="switch"
              id={`compare-site-active-${key}`} checked={!disabledSites[key]}
              disabled={saving !== null} onChange={() => toggle(key)} />
            <label className="form-check-label" htmlFor={`compare-site-active-${key}`}>{label}</label>
          </div>
        ))}
      </Modal.Body>
    </Modal>
  </>;
};

export default CompareSiteSettings;
